import { useEffect, useState } from "react";
import {
  shopModeApi,
  type FloorBouquetInStock,
  type FloorSellableItem,
} from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { errorText } from "../../i18n/serverErrors";
import { eur } from "./shopFloorFormat";
import { ShopFloorTicketLines } from "./ShopFloorTicketLines";
import { StockItemFinder } from "./StockItemFinder";
import type { TicketCart } from "./ticketCart";
import type { CustomerOrder } from "../../api/types";
import { offeredShelfBouquets, stockLeftForExtras } from "./collectExtrasRules";

/**
 * G-147 (owner 2026-10-13): "+ Add items" on "Customer collected" — goods the
 * customer adds at the counter (5 more roses, a card, a bouquet from the
 * shelf) go on the handover's own sale, so stock and money stay right on one
 * ticket. The till's finder, cart and lines; the host owns the cart.
 */
export function ShopFloorCollectExtras({
  order,
  ticket,
  disabled,
}: {
  order: Pick<CustomerOrder, "lines">;
  ticket: TicketCart;
  disabled?: boolean;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<FloorSellableItem[]>([]);
  const [bouquets, setBouquets] = useState<FloorBouquetInStock[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Loaded the first time the finder opens, then kept for this handover.
  useEffect(() => {
    if (!open || loaded) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    Promise.all([
      shopModeApi.floorListBouquetsInStock(),
      shopModeApi.floorListSellableItemsInStock(),
    ])
      .then(([nextBouquets, nextItems]) => {
        if (cancelled) return;
        setBouquets(nextBouquets);
        setItems(nextItems);
        setLoaded(true);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(errorText(error, "sale.couldNotLoadStock"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, loaded]);

  const shelfBouquets = offeredShelfBouquets(bouquets, ticket.cartedBuildIds);
  const extraStock = stockLeftForExtras(items, order);

  return (
    <div className="sf-collect-extras">
      {ticket.cart.length > 0 ? (
        <div className="sf-collect-extras-lines">
          <h4>{t("orders.addedAtCounter")}</h4>
          <ShopFloorTicketLines ticket={ticket} />
        </div>
      ) : null}

      {open ? (
        <div className="sf-collect-extras-finder">
          {loadError ? <div className="alert error">{loadError}</div> : null}
          <h4>{t("hub.bouquetsInStock")}</h4>
          {shelfBouquets.length === 0 ? (
            <div className="shop-floor-sale-inline-empty">
              {loading ? t("sale.loadingBouquets") : t("sale.noBouquets")}
            </div>
          ) : (
            <div className="shop-floor-sale-bouquet-list">
              {shelfBouquets.map((bouquet) => (
                <button
                  key={bouquet.id}
                  type="button"
                  className="shop-floor-sale-bouquet-card"
                  onClick={() =>
                    ticket.addBouquet(
                      bouquet,
                      bouquet.name || bouquet.templateName || t("sale.bouquet"),
                    )
                  }
                  disabled={disabled}
                >
                  <span className="shop-floor-sale-bouquet-plus" aria-hidden="true">+</span>
                  <span>
                    <strong>{bouquet.name || bouquet.templateName || t("sale.bouquet")}</strong>
                    <small>
                      {bouquet.suggestedSalePriceEur != null
                        ? t("sale.suggestedPrice", {
                            price: eur(Number(bouquet.suggestedSalePriceEur)),
                          })
                        : t("sale.priceNeeded")}
                    </small>
                  </span>
                </button>
              ))}
            </div>
          )}
          <h4>{t("sale.products")}</h4>
          <StockItemFinder
            items={extraStock}
            loading={loading}
            qtyInTicket={ticket.itemQtyInCart}
            onPick={ticket.addItem}
          />
          <div className="button-row">
            <button
              type="button"
              className="button plain"
              onClick={() => setOpen(false)}
              disabled={disabled}
            >
              {t("orders.doneAdding")}
            </button>
          </div>
        </div>
      ) : (
        <div className="button-row">
          <button
            type="button"
            className="button tinted"
            onClick={() => setOpen(true)}
            disabled={disabled}
          >
            {t("orders.addItems")}
          </button>
        </div>
      )}
    </div>
  );
}
